import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';

import { AuthService } from '@core/services/auth/auth.service';
import { Role } from '@models/enums/role';

/**
 * Route-Guard, der eine Route auf User einer bestimmten Rolle beschränkt.
 *
 * Nutzung in `app.routes.ts`:
 * ```ts
 * {
 *   path: 'depot',
 *   canActivate: [dashboardGuard, roleGuard],
 *   data: { role: 'customer' },
 *   component: DepotComponent,
 * }
 * ```
 *
 * Ablauf:
 * - Keine `data.role` → durchlassen.
 * - Kein eingeloggter User → `/auth/login`.
 * - Rolle passt → durchlassen.
 * - Falsche Rolle → zur passenden Shell des eingeloggten Users umleiten
 *   (Staff → `/admin/kunden`, Customer → `/depot`). Ab Sprint 3 Tag 1
 *   kein Force-Logout mehr, weil beide Rollen parallel ein vollwertiges
 *   Landing haben.
 */
export const roleGuard: CanActivateFn = async (route) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const requiredRole = route.data['role'] as Role | undefined;
  if (!requiredRole) return true;

  const currentUser = authService.user();
  if (!currentUser) {
    await router.navigate(['/auth/login']);
    return false;
  }

  if (currentUser.role === requiredRole) return true;

  // Falsche Rolle: zur passenden Shell umleiten (keine Redirect-Schleife,
  // weil das Ziel die Rolle des Users bereits erfüllt).
  const landing = currentUser.role === Role.STAFF ? '/admin/kunden' : '/depot';
  await router.navigate([landing]);
  return false;
};
